import { computed, shallowRef, watch, type ComputedRef } from 'vue';

import { applyTheme, getInitialTheme, type ThemeMode } from '@shared/theme/themeStorage';

export type ThemeContext = {
  theme: ComputedRef<ThemeMode>;
  isLight: ComputedRef<boolean>;
  themeLabel: ComputedRef<string>;
  themeClasses: ComputedRef<Record<'root' | 'header' | 'main' | 'surface' | 'muted', string>>;
  toggleTheme: () => void;
};

export function useTheme(): ThemeContext {
  const themeState = shallowRef<ThemeMode>(getInitialTheme());
  const theme = computed(() => themeState.value);
  const isLight = computed(() => themeState.value === 'light');
  const themeLabel = computed(() => (themeState.value === 'dark' ? 'Светлая тема' : 'Темная тема'));
  const themeClasses = computed(() => ({
    root: 'bg-[var(--ui-surface-muted)] text-[var(--ui-foreground)]',
    header: isLight.value
      ? 'border-b border-slate-200/80 bg-white/90 supports-[backdrop-filter]:bg-white/78'
      : 'border-b border-white/10 bg-slate-950/88 supports-[backdrop-filter]:bg-slate-950/78',
    main: 'bg-[var(--ui-surface-muted)] text-[var(--ui-foreground)]',
    surface: isLight.value
      ? 'border border-slate-200 bg-white text-slate-950 shadow-sm'
      : 'border border-white/10 bg-slate-900/78 text-white shadow-xl',
    muted: isLight.value ? 'text-slate-500' : 'text-white/60'
  }));

  const toggleTheme = () => {
    themeState.value = themeState.value === 'dark' ? 'light' : 'dark';
  };

  watch(
    themeState,
    (value) => {
      applyTheme(value);
    },
    { immediate: true }
  );

  return {
    theme,
    isLight,
    themeLabel,
    themeClasses,
    toggleTheme
  };
}
